import type {CopilotReviewerRow} from '@github-ui/dotcom-schema/types/pull_requests/v1/review_requests'
import {announce} from '@github-ui/aria-live'
import {Button} from '@primer/react'
import {Tooltip} from '@primer/react/next'
import {useRef} from 'react'
import {clsx} from 'clsx'
import styles from '../Reviewers.module.css'
import {CopilotReviewEffortMenu} from '../CopilotReviewEffortMenu'
import {ReviewerIdentity, reviewerDisplayName} from '../ReviewerIdentity'
import {useStagedCopilotReviewEffort} from '../use-staged-copilot-review-effort'
import {useModifyReviewers} from '../../../../mutations/use-modify-reviewers'
import {usePullRequestAnalytics} from '../../../../hooks/use-pull-request-analytics'

export function CopilotRequestRow({
  row,
  onMutationStart,
  onMutationError,
  onMutationSuccess,
  onCloseWithoutTarget,
}: {
  row: CopilotReviewerRow
  onMutationStart: () => void
  onMutationError: (origin: Element | null) => void
  onMutationSuccess: () => void
  onCloseWithoutTarget?: () => void
}) {
  const mutationOriginRef = useRef<Element | null>(null)
  const {mutate: modifyReviewers, isPending} = useModifyReviewers({
    onError: () => onMutationError(mutationOriginRef.current),
    onSuccess: onMutationSuccess,
  })
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()
  const {shownEffort, explicitEffort, stageEffort} = useStagedCopilotReviewEffort(row, row.requestable)
  const reviewer = row.reviewer
  if (!reviewer) return null

  const name = reviewerDisplayName(reviewer)
  const request = () => {
    if (isPending || !row.requestable) return
    mutationOriginRef.current = document.activeElement
    onMutationStart()
    announce(`Requesting review from ${name}`)
    sendPullRequestAnalyticsEvent('overview.request_copilot_review', 'OVERVIEW_REVIEWER', {
      review_effort: explicitEffort ?? 'default',
    })
    modifyReviewers({
      addReviewers: [
        {
          reviewerId: Number(reviewer.id),
          isTeam: false,
          ...(explicitEffort ? {reviewEffortLevel: explicitEffort} : {}),
        },
      ],
      removeReviewers: [],
    })
  }

  return (
    <li className={clsx(styles.reviewerRow, styles.withEffortMenu, styles.requestRow)}>
      <span className={styles.reviewerIdentityGroup}>
        <ReviewerIdentity reviewer={reviewer} />
        <span className={styles.effortMenu}>
          <CopilotReviewEffortMenu
            row={row}
            selectedEffort={shownEffort}
            disabled={isPending}
            onCloseWithoutTarget={onCloseWithoutTarget}
            onSelectEffort={effort => {
              sendPullRequestAnalyticsEvent('overview.copilot_review_effort_select', 'OVERVIEW_REVIEWER', {
                review_effort: effort,
              })
              stageEffort(effort)
            }}
          />
        </span>
      </span>
      {row.requestable ? (
        <Tooltip text={`Request review from ${name}`} type="label">
          <Button
            variant="link"
            className={styles.requestButton}
            inactive={isPending || undefined}
            aria-disabled={isPending || undefined}
            onClick={request}
          >
            Request
          </Button>
        </Tooltip>
      ) : (
        <Button variant="link" className={styles.requestButton} aria-label={`Request review from ${name}`} disabled>
          Request
        </Button>
      )}
    </li>
  )
}
